export interface DefaultInstructionsConfig {
  dataopsliveBaseUrl: string;
  authentikDomain: string;
}

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, '');

/**
 * Shared account-details table markup. `signInRow` is optional and is inserted between the URL and Username rows
 * (used by the passwordless template only), so URL / Username / Password markup stays identical across templates.
 */
const buildAccountDetails = (signInRow: string = ''): string => `## Snowflake Account Details

### Your temporary Snowflake account

<div class="mt-6 border-t border-gray-100">
  <dl class="divide-y space-y-2 divide-gray-100">
    <div class="px-4  grid grid-cols-4">
      <dt class="text-md/6 font-medium text-gray-900">URL</dt>
      <dd class="text-md/6 text-gray-700 col-span-3"><a href="https://{{vars.DATAOPS_SNOWFLAKE_ACCOUNT}}.snowflakecomputing.com" target="_blank" rel="noopener noreferrer">https://{{vars.DATAOPS_SNOWFLAKE_ACCOUNT}}.snowflakecomputing.com</a></dd>
    </div>
${signInRow}    <div class="px-4  grid grid-cols-4">
      <dt class="text-md/6 font-medium text-gray-900">Username</dt>
      <dd class="text-md/6 text-gray-700 col-span-3">{{vars.SNOWFLAKE_USER}}</dd>
    </div>
    <div class="px-4  grid grid-cols-4">
      <dt class="text-md/6 font-medium text-gray-900">Password</dt>
      <dd class="text-md/6 text-gray-700 col-span-3">{{vars.SNOWFLAKE_PASSWORD}}</dd>
    </div>
  </dl>
</div>
`;

const PASSWORDLESS_SIGN_IN_ROW = `    <div class="px-4  grid grid-cols-4">
      <dt class="text-md/6 font-medium text-gray-900">Sign in</dt>
      <dd class="text-md/6 text-gray-700 col-span-3">Use <strong>Open Snowflake</strong> above, or on the Snowflake login page choose <strong>Sign in using DataOps.live</strong>. If SSO doesn't work, use the username and password below.</dd>
    </div>
`;

/** Standard (password-based) default instructions, including the Lab Instructions button. */
export function buildStandardInstructions({ dataopsliveBaseUrl }: { dataopsliveBaseUrl: string }): string {
  const base = stripTrailingSlash(dataopsliveBaseUrl);
  return `# {{vars.EVENT_NAME}}

---

## Welcome.

Here are the step by step instructions to run the Hands on Lab:

<a href="${base}/{{vars.CONFIGURE_PROJECT_PATH_WITH_NAMESPACE}}/documentations/{{vars.CONFIGURE_PIPELINE_ID}}?job={{vars.CONFIGURE_PROJECT_HOMEPAGE_JOB_ID}}" target="_blank" rel="noopener noreferrer">
  <svg xmlns="http://www.w3.org/2000/svg" width="200" height="50">
    <rect x="0" y="0" width="200" height="50" rx="10" ry="10" fill="#11567f" />
    <text x="100" y="30" font-family="Arial, sans-serif" font-size="16" fill="white" text-anchor="middle">
        Lab Instructions
    </text>
  </svg>
</a>

<br>

${buildAccountDetails()}`;
}

/**
 * Passwordless default instructions: an Open Snowflake button that signs the attendee in through DataOps.live SSO
 * (Authentik IdP-initiated SAML), plus the username/password as a fallback. There is intentionally no Lab
 * Instructions button, because passwordless attendees have no DataOps.live platform account.
 */
export function buildPasswordlessInstructions({ authentikDomain }: { authentikDomain: string }): string {
  // COUPLING: the Jinja expression in the Open Snowflake `href` below approximates dataops-admin's `sanitize_slug()`
  // (service/event_management/services/authentik_service.py), which names each account's Authentik SAML application.
  // It covers the separator runs SSC can produce (`.` and `_` become `-`, and a `--` that appears when a truncated
  // event ID ends in `-` is collapsed to `-`). It is NOT full parity with `sanitize_slug()`.
  // It is rendered by dataops-admin (service/event_management/api/resources.py, `jinja2.Environment`).
  // If `sanitize_slug()` changes, this expression MUST be reviewed and changed with it.
  // Example: sfsehol-passwordless_event_test_hfvkji -> sfsehol-passwordless-event-test-hfvkji
  // Keep the expression inline in the `href`: a `{% set %}` block would show up as literal text in the editor
  // Preview, because MarkdownEditor's preview substitution does not render Jinja.
  const snowflakeAccountSlug = `{{ vars.DATAOPS_SNOWFLAKE_ACCOUNT | lower | replace('.', '-') | replace('_', '-') | replace('--', '-') }}`;
  return `# {{vars.EVENT_NAME}}

---

## Welcome.

Use the button below to open your Snowflake account:

<a href="https://${authentikDomain}/application/saml/${snowflakeAccountSlug}/sso/binding/init/" target="_blank" rel="noopener noreferrer">
  <svg xmlns="http://www.w3.org/2000/svg" width="260" height="50">
    <rect x="0" y="0" width="260" height="50" rx="10" ry="10" fill="#29b5e8" />
    <text x="130" y="30" font-family="Arial, sans-serif" font-size="16" fill="white" text-anchor="middle">
        Open Snowflake
    </text>
  </svg>
</a>

<br>

${buildAccountDetails(PASSWORDLESS_SIGN_IN_ROW)}`;
}

/** Picks the default instructions template matching the Passwordless toggle. */
export function buildDefaultInstructions(passwordless: boolean, config: DefaultInstructionsConfig): string {
  return passwordless ? buildPasswordlessInstructions(config) : buildStandardInstructions(config);
}
